// src/components/hall-prices/AdminReservationsManagerModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "../ui/Button";
import ConfirmModal from "../ui/ConfirmModal";
import {
    AdminReservationItem,
    AdminReservationSeat,
    cancelAdminReservationRequest,
    downloadAdminReservationLieferscheinRequest,
    getAdminReservationSeatsRequest,
    listAdminReservationsRequest,
    removeSeatFromAdminReservationRequest,
} from "../../services/api/adminReservations.api";
import {
    SchemeSeat,
    SchemeSector,
    SchemeStandingPlace,
} from "../hall-schemes/HallSchemeEditorCanvas";

type Props = {
    isOpen: boolean;
    tourCityId: string | null;
    onClose: () => void;
    onChanged: () => void; // вызвать reload в родителе после удаления/отмены
    seats: SchemeSeat[];
    standingPlaces: SchemeStandingPlace[];
    sectors: SchemeSector[];
};

type EnrichedSeat = AdminReservationSeat & {
    displaySector: string | null;
    displayRow: string | null;
    displayPlace: string | null;
};

const AdminReservationsManagerModal: React.FC<Props> = ({
    isOpen,
    tourCityId,
    onClose,
    onChanged,
    seats: schemeSeats,
    standingPlaces,
    sectors,
}) => {
    const sectorById = useMemo(
        () => new Map<string, SchemeSector>(sectors.map((s) => [s.id, s])),
        [sectors],
    );
    const seatById = useMemo(
        () => new Map<string, SchemeSeat>(schemeSeats.map((s) => [s.id, s])),
        [schemeSeats],
    );
    const standingById = useMemo(
        () =>
            new Map<string, SchemeStandingPlace>(
                standingPlaces.map((p) => [p.id, p]),
            ),
        [standingPlaces],
    );

    const enrichSeat = (s: AdminReservationSeat): EnrichedSeat => {
        const elementId = s.hall_scheme_element_id;
        const schemeSeat = elementId ? seatById.get(elementId) : undefined;
        const standing = elementId ? standingById.get(elementId) : undefined;
        let sectorId: string | null = null;
        let row: string | null = null;
        let place: string | null = null;
        if (schemeSeat) {
            sectorId = schemeSeat.sectorId ?? null;
            row = schemeSeat.rowNumber ?? null;
            place = schemeSeat.seatNumber ?? null;
        } else if (standing) {
            sectorId = standing.sectorId ?? null;
            place = standing.text || null;
        }
        // fallback на бэкендные поля, если в схеме не нашлись
        if (row == null && s.row != null) row = String(s.row);
        if (place == null && s.place != null) place = String(s.place);
        const sectorName =
            (sectorId && sectorById.get(sectorId)?.name) ||
            s.sector_name ||
            null;
        return {
            ...s,
            displaySector: sectorName,
            displayRow: row,
            displayPlace: place,
        };
    };
    const [items, setItems] = useState<AdminReservationItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [activeReservation, setActiveReservation] =
        useState<AdminReservationItem | null>(null);
    const [seats, setSeats] = useState<AdminReservationSeat[]>([]);
    const [seatsLoading, setSeatsLoading] = useState(false);
    const [seatsError, setSeatsError] = useState<string | null>(null);

    const [seatToRemove, setSeatToRemove] = useState<AdminReservationSeat | null>(
        null,
    );
    const [reservationToCancel, setReservationToCancel] =
        useState<AdminReservationItem | null>(null);

    const [actionInProgress, setActionInProgress] = useState(false);
    const [lieferscheinLoading, setLieferscheinLoading] = useState(false);
    const [lieferscheinError, setLieferscheinError] = useState<string | null>(null);

    const loadList = async () => {
        if (!tourCityId) {
            setItems([]);
            return;
        }
        setIsLoading(true);
        setLoadError(null);
        try {
            const resp = await listAdminReservationsRequest({
                tour_city_id: tourCityId,
                status: "active",
                page: 1,
                page_size: 200,
            });
            setItems(resp.items || []);
        } catch (err) {
            setLoadError(
                err instanceof Error ? err.message : "Не удалось загрузить брони.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    const loadSeats = async (reservationId: string) => {
        setSeatsLoading(true);
        setSeatsError(null);
        try {
            const list = await getAdminReservationSeatsRequest(reservationId);
            setSeats(list || []);
        } catch (err) {
            setSeatsError(
                err instanceof Error ? err.message : "Не удалось загрузить места.",
            );
        } finally {
            setSeatsLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setActiveReservation(null);
        setSeats([]);
        void loadList();
    }, [isOpen, tourCityId]);

    const handleOpenReservation = (reservation: AdminReservationItem) => {
        setActiveReservation(reservation);
        void loadSeats(reservation.id);
    };

    const handleBack = () => {
        setActiveReservation(null);
        setSeats([]);
    };

    const handleRemoveSeatConfirm = async () => {
        if (!seatToRemove || !activeReservation) return;
        setActionInProgress(true);
        try {
            await removeSeatFromAdminReservationRequest(
                activeReservation.id,
                seatToRemove.id,
            );
            await loadSeats(activeReservation.id);
            await loadList();
            onChanged();
        } catch (err) {
            setSeatsError(
                err instanceof Error ? err.message : "Не удалось удалить место.",
            );
        } finally {
            setActionInProgress(false);
            setSeatToRemove(null);
        }
    };

    const handleCancelReservationConfirm = async () => {
        if (!reservationToCancel) return;
        setActionInProgress(true);
        try {
            await cancelAdminReservationRequest(reservationToCancel.id);
            await loadList();
            onChanged();
            if (activeReservation?.id === reservationToCancel.id) {
                setActiveReservation(null);
                setSeats([]);
            }
        } catch (err) {
            setLoadError(
                err instanceof Error
                    ? err.message
                    : "Не удалось отменить бронь.",
            );
        } finally {
            setActionInProgress(false);
            setReservationToCancel(null);
        }
    };

    if (!isOpen) return null;

    const renderAgent = (r: AdminReservationItem) => {
        if (!r.user) return "—";
        const fullName = [r.user.first_name, r.user.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
        return fullName || r.user.email || "—";
    };

    return (
        <>
            <div className="modal-backdrop" role="dialog" aria-modal="true">
                <div className="modal-dialog" style={{ maxWidth: 960 }}>
                    <div className="card notopacity">
                        <div
                            className="card-body"
                            style={{ display: "flex", flexDirection: "column", gap: 12 }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <h3 className="card-title" style={{ margin: 0 }}>
                                    {activeReservation
                                        ? `Бронь: ${activeReservation.name}`
                                        : "Административные брони"}
                                </h3>
                                <div style={{ display: "flex", gap: 8 }}>
                                    {activeReservation && (
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleBack}
                                        >
                                            ← Назад
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={onClose}
                                    >
                                        Закрыть
                                    </Button>
                                </div>
                            </div>

                            {!activeReservation && (
                                <>
                                    {loadError && (
                                        <p style={{ color: "#ef4444" }}>{loadError}</p>
                                    )}

                                    {isLoading ? (
                                        <p>Загрузка...</p>
                                    ) : items.length === 0 ? (
                                        <div className="empty-block">
                                            <h3 className="empty-block-title">
                                                Броней пока нет
                                            </h3>
                                            <p className="empty-block-text">
                                                Создайте бронь — выделите места на схеме и
                                                нажмите «Административная бронь».
                                            </p>
                                        </div>
                                    ) : (
                                        <div
                                            className="table-wrap"
                                            style={{ maxHeight: 480, overflowY: "auto" }}
                                        >
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th>Название</th>
                                                        <th>Распространитель</th>
                                                        <th>Мест</th>
                                                        <th>Создана</th>
                                                        <th>Действия</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((r) => (
                                                        <tr key={r.id}>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    style={{
                                                                        background: "none",
                                                                        border: "none",
                                                                        color: "#2563eb",
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                        padding: 0,
                                                                    }}
                                                                    onClick={() => handleOpenReservation(r)}
                                                                >
                                                                    {r.name}
                                                                </button>
                                                            </td>
                                                            <td>{renderAgent(r)}</td>
                                                            <td>{r.seats_count}</td>
                                                            <td>
                                                                {new Date(r.created_at).toLocaleString()}
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    type="button"
                                                                    variant="secondary"
                                                                    onClick={() => setReservationToCancel(r)}
                                                                    disabled={actionInProgress}
                                                                >
                                                                    Отменить бронь
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}

                            {activeReservation && (
                                <>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 12,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <div>
                                            <p style={{ margin: 0 }}>
                                                Распространитель:{" "}
                                                <strong>{renderAgent(activeReservation)}</strong>
                                            </p>
                                            <p style={{ margin: 0, color: "#64748b" }}>
                                                Мест в брони:{" "}
                                                {seatsLoading ? "..." : seats.length}
                                            </p>
                                        </div>

                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                disabled={
                                                    actionInProgress ||
                                                    lieferscheinLoading ||
                                                    seats.length === 0
                                                }
                                                onClick={async () => {
                                                    if (!activeReservation) return;
                                                    setLieferscheinError(null);
                                                    setLieferscheinLoading(true);
                                                    try {
                                                        const { blob, filename } =
                                                            await downloadAdminReservationLieferscheinRequest(
                                                                activeReservation.id,
                                                            );
                                                        const url = URL.createObjectURL(blob);
                                                        const a = document.createElement("a");
                                                        a.href = url;
                                                        a.download = filename;
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        document.body.removeChild(a);
                                                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                                                    } catch (err) {
                                                        setLieferscheinError(
                                                            err instanceof Error
                                                                ? err.message
                                                                : "Не удалось скачать Lieferschein",
                                                        );
                                                    } finally {
                                                        setLieferscheinLoading(false);
                                                    }
                                                }}
                                            >
                                                {lieferscheinLoading
                                                    ? "Формируем PDF..."
                                                    : "Скачать Lieferschein"}
                                            </Button>

                                            <Button
                                                type="button"
                                                variant="danger"
                                                onClick={() =>
                                                    setReservationToCancel(activeReservation)
                                                }
                                                disabled={actionInProgress}
                                            >
                                                Отменить всю бронь
                                            </Button>
                                        </div>
                                    </div>

                                    {lieferscheinError && (
                                        <p style={{ color: "#ef4444" }}>{lieferscheinError}</p>
                                    )}

                                    {seatsError && (
                                        <p style={{ color: "#ef4444" }}>{seatsError}</p>
                                    )}

                                    {seatsLoading ? (
                                        <p>Загрузка мест...</p>
                                    ) : seats.length === 0 ? (
                                        <p style={{ color: "#64748b" }}>
                                            В брони нет мест.
                                        </p>
                                    ) : (
                                        <div
                                            className="table-wrap"
                                            style={{ maxHeight: 420, overflowY: "auto" }}
                                        >
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th>Сектор</th>
                                                        <th>Ряд</th>
                                                        <th>Место</th>
                                                        <th>Цена</th>
                                                        <th>Действия</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {seats.map((raw) => {
                                                        const s = enrichSeat(raw);
                                                        return (
                                                        <tr key={s.id}>
                                                            <td>{s.displaySector ?? "—"}</td>
                                                            <td>{s.displayRow ?? "—"}</td>
                                                            <td>{s.displayPlace ?? "—"}</td>
                                                            <td>
                                                                {s.price != null
                                                                    ? `${Math.round(Number(s.price))} €`
                                                                    : "—"}
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    type="button"
                                                                    variant="secondary"
                                                                    onClick={() => setSeatToRemove(raw)}
                                                                    disabled={actionInProgress}
                                                                >
                                                                    Удалить из брони
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {seatToRemove && (
                <ConfirmModal
                    isOpen={!!seatToRemove}
                    title="Удалить место из брони?"
                    description={(() => {
                        const e = enrichSeat(seatToRemove);
                        return `Место «${e.displaySector ?? "—"}», ряд ${e.displayRow ?? "—"}, место ${e.displayPlace ?? "—"} будет освобождено и уйдёт в продажу.`;
                    })()}
                    confirmLabel="Удалить"
                    cancelLabel="Отмена"
                    variant="danger"
                    onCancel={() => setSeatToRemove(null)}
                    onConfirm={handleRemoveSeatConfirm}
                />
            )}

            {reservationToCancel && (
                <ConfirmModal
                    isOpen={!!reservationToCancel}
                    title="Отменить бронь?"
                    description={`Все места брони «${reservationToCancel.name}» (${reservationToCancel.seats_count}) уйдут обратно в продажу.`}
                    confirmLabel="Отменить бронь"
                    cancelLabel="Отмена"
                    variant="danger"
                    onCancel={() => setReservationToCancel(null)}
                    onConfirm={handleCancelReservationConfirm}
                />
            )}
        </>
    );
};

export default AdminReservationsManagerModal;
